import { Tooltip } from "@/components/ui/tooltip";
import { PhoneOutgoing } from "lucide-react";
import React from "react";

export type Column = {
  label: string | React.ReactNode;
  key: string;
  colspan?: number;
  align?: "left" | "center" | "right";
  borderRight?: boolean;
  reducePadding?: boolean;
  increasePadding?: boolean;
  sortable?: boolean;
};

// * Sum of colspans in a group must be 21

export const handoverColumns: Column[] = [
  { label: "Customer", key: "customerName", colspan: 5 },
  { label: "Allocated To", key: "allocatedToUserId", colspan: 2 },
  { label: "Handover To", key: "handoverToUserId", colspan: 3 },
  { label: "Note", key: "note", colspan: 10 },
  {
    label: "",
    key: "",
    colspan: 1,
    align: "center",
    reducePadding: true,
    sortable: false,
  },
];

export const valCallsColumns: Column[] = [
  { label: "Customer", key: "customerName", colspan: 6 },
  { label: "App ID", key: "applicationId", colspan: 4, increasePadding: true },
  { label: "Paid Out", key: "paidOutDate", colspan: 4, align: "center" },
  {
    label: "Dealer Call",
    key: "dealerValCallResult",
    colspan: 2,
    align: "center",
  },
  {
    label: "Cust. Call 1",
    key: "customerValCall1Result",
    colspan: 2,
    align: "center",
  },
  {
    label: "Cust. Call 2",
    key: "customerValCall2Result",
    colspan: 2,
    align: "center",
  },
  {
    label: "",
    key: "",
    colspan: 1,
    align: "center",
    reducePadding: true,
    sortable: false,
  },
];

export const getWIPColumns = (pipelineView: "agent" | "department" | "all"): Column[] => {
  if (pipelineView === "department") {
    return [
      { label: "Customer", key: "customerName", colspan: 4 },
      { label: "Agent", key: "allocatedAgentName", colspan: 2 },
      {
        label: "Predicted",
        key: "isPredictedToPay",
        colspan: 2,
        align: "center",
        reducePadding: true,
      },
      { label: "Payout Agent", key: "payoutAgent", colspan: 3 },
      {
        label: "Status",
        key: "status",
        colspan: 3,
        align: "center",
        reducePadding: true,
      },
      {
        label: "Customer",
        key: "customerDocsStatus",
        colspan: 2,
        align: "center",
        reducePadding: true,
      },
      {
        label: "Dealer",
        key: "dealerDocsStatus",
        colspan: 2,
        align: "center",
        reducePadding: true,
      },
      {
        label: "Finance",
        key: "financeDocsStatus",
        colspan: 2,
        align: "center",
        reducePadding: true,
      },
      {
        label: "",
        key: "",
        colspan: 1,
        align: "center",
        reducePadding: true,
        sortable: false,
      },
    ];
  } else {
    return [
      { label: "Customer", key: "customerName", colspan: 5 },
      {
        label: "Predicted",
        key: "isPredictedToPay",
        colspan: 2,
        align: "center",
        reducePadding: true,
      },
      {
        label: "Payout Agent",
        key: "payoutAgent",
        colspan: 3,
        increasePadding: true,
      },
      {
        label: "Status",
        key: "status",
        colspan: 4,
        align: "center",
        reducePadding: true,
      },
      {
        label: "Customer",
        key: "customerDocsStatus",
        colspan: 2,
        align: "center",
        reducePadding: true,
      },
      {
        label: "Dealer",
        key: "dealerDocsStatus",
        colspan: 2,
        align: "center",
        reducePadding: true,
      },
      {
        label: "Finance",
        key: "financeDocsStatus",
        colspan: 2,
        align: "center",
        reducePadding: true,
      },
      {
        label: "",
        key: "",
        colspan: 1,
        align: "center",
        reducePadding: true,
        sortable: false,
      },
    ];
  }
};

export const customersColumns: Column[] = [
  { label: "Customer", key: "customerName", colspan: 4 },
  {
    label: "Predicted",
    key: "isPredictedToDoc",
    colspan: 2,
    align: "center",
    reducePadding: true,
  },
  { label: "Allocated", key: "allocatedOn", colspan: 2, align: "center" },
  {
    label: React.createElement(Tooltip, {
      children: React.createElement(
        "div",
        { className: "flex justify-center" },
        React.createElement(PhoneOutgoing, { size: 16 })
      ),
    }),
    key: "conversationsOutboundCount",
    colspan: 1,
    align: "center",
  },
  {
    label: "Last Conversation",
    key: "lastTouch",
    colspan: 2,
    align: "center",
    reducePadding: true,
  },
  {
    label: "Last Note",
    key: "lastNote",
    colspan: 2,
    align: "center",
    reducePadding: true,
    sortable: false,
  },
  {
    label: "Last Activity",
    key: "newActivity",
    colspan: 3,
    align: "center",
    reducePadding: true,
  },
  {
    label: "Quotes",
    key: "quotes",
    colspan: 2,
    align: "center",
    reducePadding: true,
  },
  {
    label: "Decision",
    key: "decision",
    colspan: 2,
    align: "center",
    reducePadding: true,
  },
  {
    label: "",
    key: "",
    colspan: 1,
    align: "center",
    reducePadding: true,
    sortable: false,
  },
];

export const dealsColumns: Column[] = [
  { label: "Customer", key: "customerName", colspan: 5 },
  { label: "Dealt", key: "dealt", colspan: 3 },
  { label: "Lender", key: "lender", colspan: 3, align: "center" },
  { label: "Product", key: "product", colspan: 3, align: "center" },
  { label: "Warranty", key: "warranty", colspan: 2, align: "center" },
  { label: "Paint", key: "paint", colspan: 2, align: "center" },
  { label: "Cosmetic", key: "cosmetic", colspan: 2, align: "center" },
  {
    label: "",
    key: "",
    colspan: 1,
    align: "center",
    reducePadding: true,
    sortable: false,
  },
];

export const getDocColumns = (
  firstColumnLabel: string,
  options?: { nameColspan?: number; includeRedocs?: boolean }
): Column[] => {
  const nameColspan = options?.nameColspan ?? 3;
  const includeRedocs = options?.includeRedocs ?? true;
  return [
    { label: firstColumnLabel, key: "name", colspan: nameColspan, sortable: true },
    {
      label: "Predicted",
      key: "predicted",
      colspan: 2,
      align: "center",
      sortable: true,
    },
    {
      label: "Docs from Predicted",
      key: "predictedActual",
      colspan: 2,
      align: "center",
      sortable: true,
    },
    {
      label: "Overall",
      key: "actual",
      colspan: 2,
      align: "center",
      sortable: true,
    },
    ...(includeRedocs
      ? [{ label: "Redocs", key: "totalReDoc", colspan: 2, align: "center" as const, sortable: true }]
      : []),
  ] as Column[];
};

export const getPaidColumns = (firstColumnLabel: string): Column[] => [
  { label: firstColumnLabel, key: "name", colspan: 3, sortable: true },
  {
    label: "GAP",
    key: "gap",
    colspan: 2,
    align: "center",
    sortable: true,
  },
  {
    label: "Predicted",
    key: "predicted",
    colspan: 2,
    align: "center",
    sortable: true,
  },
  {
    label: "Overall Paid",
    key: "actual",
    colspan: 2,
    align: "center",
    sortable: true,
  },
  {
    label: "% From Original GAP",
    key: "pctFromOriginalGap",
    colspan: 2,
    align: "center",
    sortable: true,
  },
  {
    label: "% Paid Overall",
    key: "pctPaidOverall",
    colspan: 2,
    align: "center",
    sortable: true,
  },
];

export const getDashboardWIPColumns = (firstColumnLabel: string): Column[] => [
  { label: firstColumnLabel, key: "name", colspan: 3, sortable: true },
  {
    label: "WIP",
    key: "docsOutBeforeOpening",
    colspan: 1,
    align: "center",
    sortable: true,
  },
  {
    label: "Ready To Go",
    key: "readyToGoBeforeOpening",
    colspan: 1,
    align: "center",
    sortable: true,
  },
  {
    label: "Payout",
    key: "payoutBeforeOpening",
    colspan: 1,
    align: "center",
    sortable: true,
  },
  {
    label: "Total",
    key: "beforeOpeningTotal",
    colspan: 1,
    align: "center",
    sortable: true,
    borderRight: true,
  },
  {
    label: "WIP",
    key: "docsOutLive",
    colspan: 1,
    align: "center",
    sortable: true,
  },
  {
    label: "Ready To Go",
    key: "readyToGoLive",
    colspan: 1,
    align: "center",
    sortable: true,
  },
  {
    label: "Payout",
    key: "payoutLive",
    colspan: 1,
    align: "center",
    sortable: true,
  },
  {
    label: "Total",
    key: "liveTotal",
    colspan: 1,
    align: "center",
    sortable: true,
  },
];

export const getAllocationColumns = (firstColumnLabel: string): Column[] => [
  { label: firstColumnLabel, key: "name", colspan: 5, sortable: true },
  {
    label: "Lead Gen",
    key: "leadGen",
    colspan: 1,
    align: "center",
    sortable: true,
  },
  {
    label: "Callbacks",
    key: "callbacks",
    colspan: 1,
    align: "center",
    sortable: true,
  },
  {
    label: "Orders",
    key: "orders",
    colspan: 1,
    align: "center",
    sortable: true,
  },
  {
    label: "WhatsApp",
    key: "whatsapp",
    colspan: 1,
    align: "center",
    sortable: true,
  },
  { label: "Other", key: "other", colspan: 1, align: "center", sortable: true },
  { label: "Total", key: "total", colspan: 1, align: "center", sortable: true },
];

export const directorDocColumns: Column[] = getDocColumns("Head of Sales");
export const directorPaidColumns: Column[] = getPaidColumns("Head of Sales");

export const wipHeaderColumns = [
  { label: "", key: "name", colspan: 3 },
  {
    label: "Start of Day GAP",
    key: "beforeOpening",
    colspan: 4,
    align: "center" as const,
  },
  { label: "Live WIP", key: "live", colspan: 4, align: "center" as const },
];

export const directorWIPColumns: Column[] = getDashboardWIPColumns("Head of Sales");
export const directorAllocationColumns: Column[] = getAllocationColumns("Head of Sales");

export const hobDocColumns: Column[] = getDocColumns("Sales Manager");
export const hobPaidColumns: Column[] = getPaidColumns("Sales Manager");
export const hobWIPHeaderColumns = wipHeaderColumns;
export const hobWIPColumns: Column[] = getDashboardWIPColumns("Sales Manager");
export const hobAllocationColumns: Column[] = getAllocationColumns("Sales Manager");

export const osmDocColumns: Column[] = getDocColumns("Team");
export const osmPaidColumns: Column[] = getPaidColumns("Team");
export const osmWIPHeaderColumns = wipHeaderColumns;
export const osmWIPColumns: Column[] = getDashboardWIPColumns("Team");

export const predictionColumns: Column[] = [
  { label: "Customer", key: "customerName", colspan: 5 },
  { label: "Status", key: "applicationStatusName", colspan: 4 },
  { label: "App ID", key: "applicationId", colspan: 3, align: "center" },
  { label: "Agent", key: "agentName", colspan: 3 },
  { label: "Department", key: "departmentName", colspan: 3 },
  { label: "Days", key: "daysSincePrediction", colspan: 3, align: "center" },
];

export const osmAllocationColumns: Column[] = getAllocationColumns("Team");

export const teamDocColumns: Column[] = getDocColumns("Agent", {
  nameColspan: 5,
  includeRedocs: false,
});
export const teamPaidColumns: Column[] = getPaidColumns("Agent");
export const teamWIPHeaderColumns = wipHeaderColumns;
export const teamWIPColumns: Column[] = getDashboardWIPColumns("Agent");
export const teamAllocationColumns: Column[] = getAllocationColumns("Agent");

export const leadershipDocColumns = directorDocColumns;
export const leadershipPaidColumns = directorPaidColumns;
export const leadershipWIPColumns = directorWIPColumns;
export const leadershipWIPHeaderColumns = wipHeaderColumns;
export const leadershipAllocationColumns = directorAllocationColumns;

export const agentsWithTLAccess = [
  13654, // Calum McMillan
  13795, // Calum McMillan
  14008, // Will O'Toole
  12216, // Paul Mawson
  14230, // Luke Horton
  12716, // Roger Cardoso
  12281, // Anthony Handley
  13876, // Mafiur Choudhury
  14342, // Max Brown
];
